/* ENSSA Reporting — student subtest table (full StudentPage view).
   Renders a real <table>: Subtest | Subtest Standard Score | Percentile Rank
   | Items correct / attempted | Correct per min. */

import { PercentileRankMiniBar } from "@/components/charts/PercentileRankMiniBar";
import { TERM_DEFAULT, getBattery } from "@/data/tokens";
import { getTaskResult } from "@/lib/report-data";
import { resolveStudentOutcomes } from "@/lib/task-family-bands";
import type { ClassRoom, Student, TaskFamilyOutcome, TermId } from "@/types";

interface StudentTaskFamilyTableProps {
  student: Student;
  klass: ClassRoom;
  term: TermId | string;
}

const TH_STYLE: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--fg-3)",
  fontWeight: 700,
  borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap",
};

const TD_BASE: React.CSSProperties = {
  padding: "14px 14px",
  borderBottom: "1px solid var(--border-subtle)",
};

function isNumberLineOutcome(o: TaskFamilyOutcome): boolean {
  const id = o.taskId?.toLowerCase() ?? "";
  return id.startsWith("bnl") || id.startsWith("unl");
}

function continuousItemsCorrect(o: TaskFamilyOutcome): string {
  return (Math.round(o.itemsCorrect * 10) / 10).toFixed(1);
}

function itemsCell(o: TaskFamilyOutcome): string {
  if (o.band === "not_attempted" || !o.rawDetailsAvailable) return "—";
  if (isNumberLineOutcome(o)) return `${continuousItemsCorrect(o)} / ${o.itemsAttempted}`;
  return `${o.itemsCorrect} / ${o.itemsAttempted}`;
}

function cpmCell(o: TaskFamilyOutcome): string {
  if (o.band === "limited" || o.band === "not_attempted" || !o.rawDetailsAvailable) return "—";
  if (!o.timed || o.correctPerMin == null) return "—";
  return `${(Math.round(o.correctPerMin * 10) / 10).toFixed(1)}`;
}

export function StudentTaskFamilyTable({
  student,
  klass,
  term,
}: StudentTaskFamilyTableProps) {
  const battery = getBattery(
    student.year,
    student.cohort || klass?.cohort || "A",
    term || TERM_DEFAULT,
  );
  const outcomes = resolveStudentOutcomes(student, battery, term);
  const rows = outcomes.map(({ task, outcome }) => ({
    task,
    outcome,
    result: getTaskResult(student, term, task.id),
  }));
  const hasSubtestScores = rows.some(({ outcome, result }) =>
    result.subtestScore != null && outcome.band !== "limited" && outcome.band !== "not_attempted",
  );

  return (
    <div className="subtest-table-body" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
        <thead style={{ background: "var(--ink-50)" }}>
          <tr>
            <th style={{ ...TH_STYLE, width: hasSubtestScores ? "24%" : "28%" }}>Subtest</th>
            {hasSubtestScores && <th style={{ ...TH_STYLE, width: "16%", textAlign: "center" }}>Subtest Standard Score</th>}
            <th style={{ ...TH_STYLE, width: hasSubtestScores ? "20%" : "24%", textAlign: "center" }}>Percentile Rank</th>
            <th style={{ ...TH_STYLE, width: hasSubtestScores ? "22%" : "28%", textAlign: "center" }}>Items Correct / Attempted</th>
            <th style={{ ...TH_STYLE, width: hasSubtestScores ? "18%" : "20%", textAlign: "center" }}>Correct per min</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ task, outcome, result }) => (
            <tr key={task.id}>
              <td style={{ ...TD_BASE, fontWeight: 600, fontSize: 14, color: "var(--fg-1)" }}>
                {task.label}
              </td>
              {hasSubtestScores && (
                <td style={{ ...TD_BASE, fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--fg-2)", textAlign: "center" }}>
                  {result.subtestScore == null || outcome.band === "limited" || outcome.band === "not_attempted" ? "Pending" : Math.round(result.subtestScore)}
                </td>
              )}
              <td style={{ ...TD_BASE, textAlign: "center" }}>
                <PercentileRankMiniBar
                  percentile={outcome.band === "limited" || outcome.band === "not_attempted" ? null : outcome.percentile}
                  pendingLabel="Pending"
                  width={112}
                />
              </td>
              <td style={{ ...TD_BASE, fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--fg-2)", textAlign: "center" }}>
                {itemsCell(outcome)}
              </td>
              <td style={{ ...TD_BASE, fontFamily: "var(--font-mono)", fontSize: 13, color: outcome.timed ? "var(--fg-2)" : "var(--fg-4)", textAlign: "center" }}>
                {cpmCell(outcome)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
